import { Box, Button, Divider, useToast } from "@chakra-ui/react";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import React, { useCallback } from "react";
import * as Yup from "yup";
import {
  REQUIRED_FIELD,
  TOKEN_MIN_LENGTH_10,
} from "constants/validator-messages";
import { AccountIntegrationTypeEnum } from "enums/account-integration-type.enum";
import { observer } from "mobx-react";
import { AccountIntegrationsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrations.store";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { ApiMessageStack, FormControlV2 } from "components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TDripConfig } from "types/account-integration.type";

type TFieldValues = {
  dripApiKey: string;
  dripAccountId: string;
};

const initialValues: TFieldValues = {
  dripApiKey: "",
  dripAccountId: "",
};

const validationSchema = Yup.object().shape({
  dripApiKey: Yup.string()
    .min(10, TOKEN_MIN_LENGTH_10)
    .required(REQUIRED_FIELD),
  dripAccountId: Yup.string().required(REQUIRED_FIELD),
});

interface IProps {
  closePrompt: () => void;
  accountIntegrationsStore: AccountIntegrationsStore;
}
export const ConnectDripForm: React.FC<IProps> = observer(
  ({ closePrompt, accountIntegrationsStore }) => {
    const toast = useToast();

    const {
      handleSubmit,
      control,
      formState: { isSubmitting },
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    const onSubmit = useCallback(
      async ({ dripApiKey, dripAccountId }: TFieldValues) => {
        try {
          await accountIntegrationsStore.createAccountIntegration<TDripConfig>({
            type: AccountIntegrationTypeEnum.DRIP,
            active: true,
            integrationConfig: { dripApiKey, dripAccountId },
          });

          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: (
              <ApiMessageStack messageStack={"Drip successfully connected"} />
            ),
          });
          closePrompt();
        } catch (e) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={e.message} />,
          });
        }
      },
      [accountIntegrationsStore, closePrompt, toast]
    );

    return (
      <Box pt={5}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={5}>
            <FormControlV2<TFieldValues>
              name={"dripApiKey"}
              control={control}
              label={"Drip API Token"}
              type={FormControlsTypeEnum.TEXT}
              additionalProps={{
                placeholder: "Drip API Token",
              }}
            />
          </Box>
          <Box mb={5}>
            <FormControlV2<TFieldValues>
              name={"dripAccountId"}
              control={control}
              label={"Drip Account ID"}
              type={FormControlsTypeEnum.TEXT}
              additionalProps={{
                placeholder: "Drip Account ID",
              }}
            />
          </Box>
          <Divider mb={4} />
          <Box
            width={"100%"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <Button
              type={"submit"}
              colorScheme={"blue"}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Connect
            </Button>
          </Box>
        </form>
      </Box>
    );
  }
);
